// src/pages/MainApp.js
import React, { useState, useEffect, useRef, useContext } from 'react';
import axios from 'axios';
import { useTheme } from '@mui/material/styles';
import { ThemeModeContext } from '../theme/themeContext';
import { AuthContext } from '../auth/AuthContext'; // --- IMPORT AuthContext

import { useFileHandler } from '../hooks/useFileHandler';
import ImageGallery from '../components/ImageGallery';
import ImageDropzone from '../components/ImageDropzone';
import ExampleImages from '../components/ExampleImages';
import AboutComponent, { LimitationsComponent } from '../components/AboutComponent';
import AuthComponent from '../components/AuthComponent';
import MainAppControlComponent from '../components/MainAppControlComponent';
import ProfileComponent from '../components/ProfileComponent';
import FooterComponent from '../components/FooterComponent';

import { AppBar, Toolbar, Typography, Box, Alert, IconButton, Button } from '@mui/material';
import { Link } from 'react-router-dom';

import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';

const API_URL = '/api'; //'http://localhost:8000';

function MainApp() {
  // --- LOCAL COMPONENT STATE ---
  // Local state for login form inputs
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  // State specific to this page's functionality
  const [message, setMessage] = useState(null);
  const [processingResults, setProcessingResults] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedFaceId, setSelectedFaceId] = useState(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [usePublicDataset, setUsePublicDataset] = useState(true);
  const [prior, setPrior] = useState('uniform');

  // --- HOOKS & CONTEXT---
  // Consume the global authentication context
  const { authState, login, logout } = useContext(AuthContext);

  // Consume the global theme context
  const themeMode = useContext(ThemeModeContext);
  const theme = useTheme();

  // Custom hook for file handling
  const {
    selectedFiles,
    imageUrls,
    handleFileUpload,
    handleRemoveImage,
    resetFiles
  } = useFileHandler(setMessage);

  // --- REFS for Sizing ---
  const mainContentRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const updateSize = () => {
      if (mainContentRef.current) {
        const padding = 40;
        setContainerSize({
          width: mainContentRef.current.clientWidth - padding,
          height: mainContentRef.current.clientHeight - padding,
        });
      }
    };
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, [processingResults]);

  // --- HANDLERS using global auth ---
  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const formData = new URLSearchParams();
      formData.append('username', username);
      formData.append('password', password);
      const response = await axios.post(`${API_URL}/token`, formData);
      const newToken = response.data.access_token;

      // Use the global login function from context
      login({ username: username }, newToken);

      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setPassword('');
      setMessage({ text: 'Logged in successfully!', severity: 'success' });
    } catch (error) {
      setMessage({ text: `Login failed: ${error.response?.data?.detail || 'Unknown error'}`, severity: 'error' });
    }
  };

  const handleLogout = () => {
    // Use the global logout function from context
    logout();
    axios.defaults.headers.common['Authorization'] = null;
    setUsername(''); // Clear username field on logout
    handleReset(); // Reset the rest of the component state
    setMessage({ text: 'You have been logged out.', severity: 'info' });
  };

  const handleReset = () => {
    resetFiles();
    setProcessingResults(null);
    setSelectedFaceId(null);
    setCurrentSlideIndex(0);
    setMessage(null);
  };

  const handleProcessImages = async () => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    setIsProcessing(true);
    setProcessingResults(null);
    setSelectedFaceId(null);
    setMessage({ text: 'Processing... this may take a moment.', severity: 'info' });

    const formData = new FormData();
    for (let i = 0; i < selectedFiles.length; i++) {
      formData.append("files", selectedFiles[i]);
    }

    if (usePublicDataset) {
      formData.append("prior", prior);
    }

    try {
      const response = await axios.post(`${API_URL}/process-images/`, formData);
      setProcessingResults(response.data.processed_results);
      setCurrentSlideIndex(0);
      setMessage({ text: 'Processing successful! Click on a face to see details.', severity: 'success' });
    } catch (error) {
      let errorMessage = 'An unknown error occurred.';
      if (error.response) errorMessage = error.response.data.detail || error.response.data.error || JSON.stringify(error.response.data);
      else if (error.request) errorMessage = 'No response from server. Is the backend running?';
      else errorMessage = error.message;
      setMessage({ text: `Processing failed: ${errorMessage}`, severity: 'error' });
      setProcessingResults(null);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFaceClick = (faceId) => setSelectedFaceId(prevId => (prevId === faceId ? null : faceId));
  const handleStageClick = () => setSelectedFaceId(null);
  const handleSlideChange = (index) => { setSelectedFaceId(null); setCurrentSlideIndex(index); };

  const handleExampleImageSelect = async (imageUrl, imageName) => {
    setMessage({ text: `Loading example: ${imageName}...`, severity: 'info' });
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const filename = imageUrl.substring(imageUrl.lastIndexOf('/') + 1) || 'example.jpg';
      const file = new File([blob], filename, { type: blob.type });
      onFilesAdded([file]);
    } catch (error) {
      console.error("Error loading example image:", error);
      setMessage({ text: 'Failed to load the example image.', severity: 'error' });
    }
  };

  const selectedFaceData = processingResults && selectedFaceId !== null && processingResults[currentSlideIndex] ? processingResults[currentSlideIndex].faces.find(f => f.id === selectedFaceId) : null;

  const onFilesAdded = (files) => {
    handleFileUpload(files);
    setProcessingResults(null);
    setSelectedFaceId(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" color="default" sx={{ flexShrink: 0 }}>
        <Toolbar>

          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img
              src="logo.svg"
              alt="University Logo"
              style={{ height: '40px', marginRight: '16px' }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" component="div" sx={{ lineHeight: 1.2 }}>
                Photo Dating by Facial Age Aggregation
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Machine Learning Group, Czech Technical University in Prague
              </Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            color="secondary"
            component={Link}
            to="/custom"
          >
            Pro Mode
          </Button>

          <IconButton sx={{ ml: 1 }} onClick={themeMode.toggleThemeMode} color="inherit">
            {theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>

          {/* Pass the global state and handlers to the AuthComponent */}
          <AuthComponent
            token={authState.token}
            loggedInUser={authState.user?.username}
            handleLogin={handleLogin}
            handleLogout={handleLogout}
            username={username}
            setUsername={setUsername}
            password={password}
            setPassword={setPassword}
          />
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        <Box component="aside" sx={{ width: 320, p: 2, flexShrink: 0, overflowY: 'auto', bgcolor: 'background.paper' }}>
          {message && <Alert severity={message.severity} sx={{ mb: 2 }}>{message.text}</Alert>}
          <MainAppControlComponent
            handleFileUpload={onFilesAdded}
            handleProcessImages={handleProcessImages}
            handleReset={handleReset}
            isProcessing={isProcessing}
            hasFiles={selectedFiles && selectedFiles.length > 0}
            usePublicDataset={usePublicDataset}
            setUsePublicDataset={setUsePublicDataset}
            prior={prior}
            setPrior={setPrior}
          />
          <ProfileComponent selectedFace={selectedFaceData} onCancel={() => setSelectedFaceId(null)} />
          <AboutComponent />
          <LimitationsComponent />
        </Box>

        <Box component="main" sx={{ flexGrow: 1, p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }} ref={mainContentRef}>
          {selectedFiles.length === 0 ? (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <ExampleImages onExampleSelect={handleExampleImageSelect} />
              <ImageDropzone onFilesAdded={onFilesAdded} />
            </Box>
          ) : (
            <ImageGallery
              results={processingResults || selectedFiles.map(f => ({ filename: f.name, faces: [] }))}
              imageUrls={imageUrls}
              selectedFaceId={selectedFaceId}
              onFaceClick={handleFaceClick}
              onStageClick={handleStageClick}
              onSlideChange={handleSlideChange}
              containerSize={containerSize}
              onRemoveImage={handleRemoveImage}
              isProcessed={!!processingResults}
              isProcessing={isProcessing}
            />
          )}
        </Box>
      </Box>
      <FooterComponent />
    </Box>
  );
}

export default MainApp;
